import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Menu, MenuItem } from '@mui/material';
import useTranslate from '../../../../../customHooks/useTranslate';
import { LinearProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import { useZitoneoUI } from '../../../contexts/ZitoneoUIContext';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import IconButton from '../../../../../muiComponents/IconButton';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import {
    useZitoneoHotelRooms,
    useZitoneoServices,
    useZitoneoServicesStats,
} from '../../../contexts/ZitoneoContentContext';
import {
    ZinantisServiceBaseState,
    VisibleContentScope,
} from '../../../types/zitoneoServicesTypes';

const CategoriesMenu = () => {
    const translate = useTranslate();
    const {
        isLoading,
        selectedCategory,
        handleSelectCategory,
        visibleContentScope,
        handleVisibleContentScope,
        selectedRoomId,
    } = useZitoneoUI();
    const [isMenuVisible, setIsMenuVisible] = useState(true);
    const [serviceMenuAnchor, setServiceMenuAnchor] =
        useState<null | HTMLElement>(null);
    const {
        HotelServices,

        ComplementaryServiceCategories,
        ComplementaryServiceTypes,
    } = useZitoneoServices();
    const { totalServices, selectedServices } = useZitoneoServicesStats();
    const { rooms } = useZitoneoHotelRooms();
    const selectedRoom = rooms.find(room => room.Id === selectedRoomId);

    const categories = useMemo(() => {
        const services: ZinantisServiceBaseState[] =
            visibleContentScope === VisibleContentScope.HOTEL
                ? HotelServices
                : selectedRoom?.Services || [];

        return ComplementaryServiceCategories.map(category => {
            const servicesInCategory = ComplementaryServiceTypes.filter(
                service => service.IdServiceCategory === parseInt(category.Id)
            );
            const selectedServiceTypes = new Set(
                services
                    .filter(service =>
                        servicesInCategory.some(
                            categoryService =>
                                parseInt(categoryService.Id) ===
                                service.IdServiceType
                        )
                    )
                    .map(
                        (service: ZinantisServiceBaseState) =>
                            service.IdServiceType
                    )
            );
            return {
                ...category,
                TotalServices: servicesInCategory.length,
                SelectedServices: selectedServiceTypes.size,
            };
        });
    }, [
        ComplementaryServiceCategories,
        ComplementaryServiceTypes,
        HotelServices,
        selectedRoom,
        visibleContentScope,
    ]);

    useEffect(() => {
        const selectedCategoryIsVisible = categories.some(
            category => category.Id === selectedCategory?.Id
        );

        if (categories.length && !selectedCategoryIsVisible) {
            handleSelectCategory(categories[0]);
        } else if (!categories.length && selectedCategory) {
            handleSelectCategory(null);
        }
    }, [categories, selectedCategory, handleSelectCategory]);

    const renderCategoryItem = (category: (typeof categories)[number]) => (
        <li
            key={category.Id}
            className={`categories-menu__list-item ${
                selectedCategory && selectedCategory.Id === category.Id
                    ? '--selected'
                    : ''
            }`}
            onClick={() => handleSelectCategory(category)}
        >
            <div
                className={`category-item__icon ${
                    selectedCategory && selectedCategory.Id === category.Id
                        ? '--selected'
                        : ''
                }`}
            >
                <Icon icon={category.Icon} />
            </div>
            <div className='category-item__main'>
                <div className='category-item__name__container'>
                    <p>{category.Name}</p>{' '}
                    <p>
                        {category.SelectedServices} / {category.TotalServices}
                    </p>
                </div>
                <div className='category-item__progress'>
                    <LinearProgress
                        variant='determinate'
                        className={
                            selectedCategory &&
                            selectedCategory.Id === category.Id
                                ? '--selected'
                                : ''
                        }
                        value={
                            category.TotalServices > 0
                                ? (category.SelectedServices /
                                      category.TotalServices) *
                                  100
                                : 0
                        }
                    />
                </div>
            </div>
        </li>
    );

    const selectedCategoryWithStats = selectedCategory
        ? categories.find(category => category.Id === selectedCategory.Id)
        : null;

    const handleServiceMenuOpen = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        setServiceMenuAnchor(event.currentTarget);
    };

    const handleServiceMenuClose = () => {
        setServiceMenuAnchor(null);
    };

    const handleServiceForChange = (contentScope: VisibleContentScope) => {
        handleVisibleContentScope(
            contentScope,
            selectedRoomId || rooms[0]?.Id || null
        );
        handleServiceMenuClose();
    };

    if (isLoading) {
        return (
            <div className='categories-menu__container'>
                <LoadingPlaceholder
                    variant='rounded'
                    width={275}
                    height={600}
                    style={{ margin: '30px 14px' }}
                />
            </div>
        );
    }

    return (
        <div
            className={`categories-menu__container ${
                isMenuVisible ? '--expanded' : '--collapsed'
            }`}
        >
            <header className='categories-menu__header'>
                <div className='flex flex-between flex-align-center'>
                    <h5 className='categories-menu__overtitle'>
                        {translate('content')}
                    </h5>
                    <IconButton
                        iconName={
                            isMenuVisible
                                ? commonIcons.ARROW_UP
                                : commonIcons.ARROW_DOWN
                        }
                        onClick={() => setIsMenuVisible(visible => !visible)}
                        aria-expanded={isMenuVisible}
                        size='small'
                    />
                </div>
                <Collapse in={isMenuVisible} timeout='auto' unmountOnExit>
                    <button
                        type='button'
                        className='categories-menu__title-selector'
                        onClick={handleServiceMenuOpen}
                        aria-haspopup='menu'
                        aria-expanded={Boolean(serviceMenuAnchor)}
                    >
                        {translate(
                            visibleContentScope === VisibleContentScope.HOTEL
                                ? 'hotel_services'
                                : 'room_services_zitoneo'
                        )}
                    </button>
                    <Menu
                        anchorEl={serviceMenuAnchor}
                        open={Boolean(serviceMenuAnchor)}
                        onClose={handleServiceMenuClose}
                    >
                        <MenuItem
                            selected={
                                visibleContentScope ===
                                VisibleContentScope.HOTEL
                            }
                            onClick={() =>
                                handleServiceForChange(
                                    VisibleContentScope.HOTEL
                                )
                            }
                        >
                            {translate('hotel_services')}
                        </MenuItem>
                        <MenuItem
                            selected={
                                visibleContentScope === VisibleContentScope.ROOM
                            }
                            onClick={() =>
                                handleServiceForChange(VisibleContentScope.ROOM)
                            }
                        >
                            {translate('room_services_zitoneo')}
                        </MenuItem>
                    </Menu>
                    <h6 className='categories-menu__subtitle'>
                        <span className='--selected'>{selectedServices}</span> /{' '}
                        {totalServices} {translate('services_selected')}
                    </h6>
                </Collapse>
            </header>
            {!isMenuVisible && selectedCategoryWithStats && (
                <div className='categories-menu__selected'>
                    <ul className='categories-menu__list'>
                        {renderCategoryItem(selectedCategoryWithStats)}
                    </ul>
                </div>
            )}
            <Collapse
                className='categories-menu__list-collapse'
                in={isMenuVisible}
                timeout='auto'
                unmountOnExit
            >
                <ul className='categories-menu__list'>
                    {categories.map(renderCategoryItem)}
                </ul>
            </Collapse>
        </div>
    );
};

export default CategoriesMenu;
